/**
 * Address Validation Library
 * VOY-227: Implements country-aware address validation
 */

// Country address configuration
export interface CountryAddressConfig {
  code: string;           // ISO 3166-1 alpha-2 country code
  name: string;           // Country name
  flag: string;           // Emoji flag
  postalCodePattern?: RegExp;      // Postal code validation regex
  postalCodeFormat: string;        // Human-readable format
  postalCodePlaceholder: string;   // Input placeholder
  postalCodeRequired: boolean;     // Whether postal code is required
  hasStates: boolean;              // Whether country has states/provinces
  stateLabel: string;              // Label for state field (State, Province, Region, etc.)
  states?: { code: string; name: string }[]; // List of states if applicable
}

// US States
const US_STATES = [
  { code: 'AL', name: 'Alabama' },
  { code: 'AK', name: 'Alaska' },
  { code: 'AZ', name: 'Arizona' },
  { code: 'AR', name: 'Arkansas' },
  { code: 'CA', name: 'California' },
  { code: 'CO', name: 'Colorado' },
  { code: 'CT', name: 'Connecticut' },
  { code: 'DE', name: 'Delaware' },
  { code: 'DC', name: 'District of Columbia' },
  { code: 'FL', name: 'Florida' },
  { code: 'GA', name: 'Georgia' },
  { code: 'HI', name: 'Hawaii' },
  { code: 'ID', name: 'Idaho' },
  { code: 'IL', name: 'Illinois' },
  { code: 'IN', name: 'Indiana' },
  { code: 'IA', name: 'Iowa' },
  { code: 'KS', name: 'Kansas' },
  { code: 'KY', name: 'Kentucky' },
  { code: 'LA', name: 'Louisiana' },
  { code: 'ME', name: 'Maine' },
  { code: 'MD', name: 'Maryland' },
  { code: 'MA', name: 'Massachusetts' },
  { code: 'MI', name: 'Michigan' },
  { code: 'MN', name: 'Minnesota' },
  { code: 'MS', name: 'Mississippi' },
  { code: 'MO', name: 'Missouri' },
  { code: 'MT', name: 'Montana' },
  { code: 'NE', name: 'Nebraska' },
  { code: 'NV', name: 'Nevada' },
  { code: 'NH', name: 'New Hampshire' },
  { code: 'NJ', name: 'New Jersey' },
  { code: 'NM', name: 'New Mexico' },
  { code: 'NY', name: 'New York' },
  { code: 'NC', name: 'North Carolina' },
  { code: 'ND', name: 'North Dakota' },
  { code: 'OH', name: 'Ohio' },
  { code: 'OK', name: 'Oklahoma' },
  { code: 'OR', name: 'Oregon' },
  { code: 'PA', name: 'Pennsylvania' },
  { code: 'PR', name: 'Puerto Rico' },
  { code: 'RI', name: 'Rhode Island' },
  { code: 'SC', name: 'South Carolina' },
  { code: 'SD', name: 'South Dakota' },
  { code: 'TN', name: 'Tennessee' },
  { code: 'TX', name: 'Texas' },
  { code: 'UT', name: 'Utah' },
  { code: 'VT', name: 'Vermont' },
  { code: 'VI', name: 'Virgin Islands' },
  { code: 'VA', name: 'Virginia' },
  { code: 'WA', name: 'Washington' },
  { code: 'WV', name: 'West Virginia' },
  { code: 'WI', name: 'Wisconsin' },
  { code: 'WY', name: 'Wyoming' },
];

// Canadian Provinces
const CA_PROVINCES = [
  { code: 'AB', name: 'Alberta' },
  { code: 'BC', name: 'British Columbia' },
  { code: 'MB', name: 'Manitoba' },
  { code: 'NB', name: 'New Brunswick' },
  { code: 'NL', name: 'Newfoundland and Labrador' },
  { code: 'NS', name: 'Nova Scotia' },
  { code: 'NT', name: 'Northwest Territories' },
  { code: 'NU', name: 'Nunavut' },
  { code: 'ON', name: 'Ontario' },
  { code: 'PE', name: 'Prince Edward Island' },
  { code: 'QC', name: 'Quebec' },
  { code: 'SK', name: 'Saskatchewan' },
  { code: 'YT', name: 'Yukon' },
];

// Australian States
const AU_STATES = [
  { code: 'ACT', name: 'Australian Capital Territory' },
  { code: 'NSW', name: 'New South Wales' },
  { code: 'NT', name: 'Northern Territory' },
  { code: 'QLD', name: 'Queensland' },
  { code: 'SA', name: 'South Australia' },
  { code: 'TAS', name: 'Tasmania' },
  { code: 'VIC', name: 'Victoria' },
  { code: 'WA', name: 'Western Australia' },
];

// Mexican States
const MX_STATES = [
  { code: 'AGU', name: 'Aguascalientes' },
  { code: 'BCN', name: 'Baja California' },
  { code: 'BCS', name: 'Baja California Sur' },
  { code: 'CAM', name: 'Campeche' },
  { code: 'CHP', name: 'Chiapas' },
  { code: 'CHH', name: 'Chihuahua' },
  { code: 'COA', name: 'Coahuila' },
  { code: 'COL', name: 'Colima' },
  { code: 'CMX', name: 'Ciudad de México' },
  { code: 'DUR', name: 'Durango' },
  { code: 'GUA', name: 'Guanajuato' },
  { code: 'GRO', name: 'Guerrero' },
  { code: 'HID', name: 'Hidalgo' },
  { code: 'JAL', name: 'Jalisco' },
  { code: 'MEX', name: 'México' },
  { code: 'MIC', name: 'Michoacán' },
  { code: 'MOR', name: 'Morelos' },
  { code: 'NAY', name: 'Nayarit' },
  { code: 'NLE', name: 'Nuevo León' },
  { code: 'OAX', name: 'Oaxaca' },
  { code: 'PUE', name: 'Puebla' },
  { code: 'QUE', name: 'Querétaro' },
  { code: 'ROO', name: 'Quintana Roo' },
  { code: 'SLP', name: 'San Luis Potosí' },
  { code: 'SIN', name: 'Sinaloa' },
  { code: 'SON', name: 'Sonora' },
  { code: 'TAB', name: 'Tabasco' },
  { code: 'TAM', name: 'Tamaulipas' },
  { code: 'TLA', name: 'Tlaxcala' },
  { code: 'VER', name: 'Veracruz' },
  { code: 'YUC', name: 'Yucatán' },
  { code: 'ZAC', name: 'Zacatecas' },
];

// Argentine Provinces
const AR_PROVINCES = [
  { code: 'C', name: 'Ciudad Autónoma de Buenos Aires' },
  { code: 'B', name: 'Buenos Aires' },
  { code: 'K', name: 'Catamarca' },
  { code: 'H', name: 'Chaco' },
  { code: 'U', name: 'Chubut' },
  { code: 'X', name: 'Córdoba' },
  { code: 'W', name: 'Corrientes' },
  { code: 'E', name: 'Entre Ríos' },
  { code: 'P', name: 'Formosa' },
  { code: 'Y', name: 'Jujuy' },
  { code: 'L', name: 'La Pampa' },
  { code: 'F', name: 'La Rioja' },
  { code: 'M', name: 'Mendoza' },
  { code: 'N', name: 'Misiones' },
  { code: 'Q', name: 'Neuquén' },
  { code: 'R', name: 'Río Negro' },
  { code: 'A', name: 'Salta' },
  { code: 'J', name: 'San Juan' },
  { code: 'D', name: 'San Luis' },
  { code: 'Z', name: 'Santa Cruz' },
  { code: 'S', name: 'Santa Fe' },
  { code: 'G', name: 'Santiago del Estero' },
  { code: 'V', name: 'Tierra del Fuego' },
  { code: 'T', name: 'Tucumán' },
];

// Brazilian States
const BR_STATES = [
  { code: 'AC', name: 'Acre' },
  { code: 'AL', name: 'Alagoas' },
  { code: 'AP', name: 'Amapá' },
  { code: 'AM', name: 'Amazonas' },
  { code: 'BA', name: 'Bahia' },
  { code: 'CE', name: 'Ceará' },
  { code: 'DF', name: 'Distrito Federal' },
  { code: 'ES', name: 'Espírito Santo' },
  { code: 'GO', name: 'Goiás' },
  { code: 'MA', name: 'Maranhão' },
  { code: 'MT', name: 'Mato Grosso' },
  { code: 'MS', name: 'Mato Grosso do Sul' },
  { code: 'MG', name: 'Minas Gerais' },
  { code: 'PA', name: 'Pará' },
  { code: 'PB', name: 'Paraíba' },
  { code: 'PR', name: 'Paraná' },
  { code: 'PE', name: 'Pernambuco' },
  { code: 'PI', name: 'Piauí' },
  { code: 'RJ', name: 'Rio de Janeiro' },
  { code: 'RN', name: 'Rio Grande do Norte' },
  { code: 'RS', name: 'Rio Grande do Sul' },
  { code: 'RO', name: 'Rondônia' },
  { code: 'RR', name: 'Roraima' },
  { code: 'SC', name: 'Santa Catarina' },
  { code: 'SP', name: 'São Paulo' },
  { code: 'SE', name: 'Sergipe' },
  { code: 'TO', name: 'Tocantins' },
];

// Comprehensive country address configurations
export const COUNTRY_ADDRESS_CONFIGS: CountryAddressConfig[] = [
  // North America
  {
    code: 'US',
    name: 'United States',
    flag: '🇺🇸',
    postalCodePattern: /^\d{5}(-\d{4})?$/,
    postalCodeFormat: '12345 or 12345-6789',
    postalCodePlaceholder: '12345',
    postalCodeRequired: true,
    hasStates: true,
    stateLabel: 'State',
    states: US_STATES,
  },
  {
    code: 'CA',
    name: 'Canada',
    flag: '🇨🇦',
    postalCodePattern: /^[A-Za-z]\d[A-Za-z][ -]?\d[A-Za-z]\d$/,
    postalCodeFormat: 'A1A 1A1',
    postalCodePlaceholder: 'A1A 1A1',
    postalCodeRequired: true,
    hasStates: true,
    stateLabel: 'Province',
    states: CA_PROVINCES,
  },
  {
    code: 'MX',
    name: 'Mexico',
    flag: '🇲🇽',
    postalCodePattern: /^\d{5}$/,
    postalCodeFormat: '12345',
    postalCodePlaceholder: '12345',
    postalCodeRequired: true,
    hasStates: true,
    stateLabel: 'State',
    states: MX_STATES,
  },
  
  // South America
  {
    code: 'AR',
    name: 'Argentina',
    flag: '🇦🇷',
    postalCodePattern: /^([A-Z]\d{4}[A-Z]{3}|\d{4})$/i,
    postalCodeFormat: '1234 or A1234ABC',
    postalCodePlaceholder: '1234',
    postalCodeRequired: true,
    hasStates: true,
    stateLabel: 'Province',
    states: AR_PROVINCES,
  },
  {
    code: 'BR',
    name: 'Brazil',
    flag: '🇧🇷',
    postalCodePattern: /^\d{5}-?\d{3}$/,
    postalCodeFormat: '12345-678',
    postalCodePlaceholder: '12345-678',
    postalCodeRequired: true,
    hasStates: true,
    stateLabel: 'State',
    states: BR_STATES,
  },
  {
    code: 'CL',
    name: 'Chile',
    flag: '🇨🇱',
    postalCodePattern: /^\d{7}$/,
    postalCodeFormat: '1234567',
    postalCodePlaceholder: '1234567',
    postalCodeRequired: false,
    hasStates: false,
    stateLabel: 'Region',
  },
  {
    code: 'CO',
    name: 'Colombia',
    flag: '🇨🇴',
    postalCodePattern: /^\d{6}$/,
    postalCodeFormat: '123456',
    postalCodePlaceholder: '123456',
    postalCodeRequired: false,
    hasStates: false,
    stateLabel: 'Department',
  },
  {
    code: 'PE',
    name: 'Peru',
    flag: '🇵🇪',
    postalCodePattern: /^\d{5}$/,
    postalCodeFormat: '12345',
    postalCodePlaceholder: '12345',
    postalCodeRequired: false,
    hasStates: false,
    stateLabel: 'Region',
  },
  {
    code: 'VE',
    name: 'Venezuela',
    flag: '🇻🇪',
    postalCodePattern: /^\d{4}$/,
    postalCodeFormat: '1234',
    postalCodePlaceholder: '1234',
    postalCodeRequired: false,
    hasStates: false,
    stateLabel: 'State',
  },
  {
    code: 'EC',
    name: 'Ecuador',
    flag: '🇪🇨',
    postalCodePattern: /^\d{6}$/,
    postalCodeFormat: '123456',
    postalCodePlaceholder: '123456',
    postalCodeRequired: false,
    hasStates: false,
    stateLabel: 'Province',
  },
  {
    code: 'UY',
    name: 'Uruguay',
    flag: '🇺🇾',
    postalCodePattern: /^\d{5}$/,
    postalCodeFormat: '12345',
    postalCodePlaceholder: '12345',
    postalCodeRequired: false,
    hasStates: false,
    stateLabel: 'Department',
  },
  
  // Europe
  {
    code: 'GB',
    name: 'United Kingdom',
    flag: '🇬🇧',
    postalCodePattern: /^[A-Z]{1,2}\d[A-Z\d]? ?\d[A-Z]{2}$/i,
    postalCodeFormat: 'SW1A 1AA',
    postalCodePlaceholder: 'SW1A 1AA',
    postalCodeRequired: true,
    hasStates: false,
    stateLabel: 'County',
  },
  {
    code: 'DE',
    name: 'Germany',
    flag: '🇩🇪',
    postalCodePattern: /^\d{5}$/,
    postalCodeFormat: '12345',
    postalCodePlaceholder: '12345',
    postalCodeRequired: true,
    hasStates: false,
    stateLabel: 'State',
  },
  {
    code: 'FR',
    name: 'France',
    flag: '🇫🇷',
    postalCodePattern: /^\d{5}$/,
    postalCodeFormat: '12345',
    postalCodePlaceholder: '75001',
    postalCodeRequired: true,
    hasStates: false,
    stateLabel: 'Region',
  },
  {
    code: 'IT',
    name: 'Italy',
    flag: '🇮🇹',
    postalCodePattern: /^\d{5}$/,
    postalCodeFormat: '12345',
    postalCodePlaceholder: '00100',
    postalCodeRequired: true,
    hasStates: false,
    stateLabel: 'Province',
  },
  {
    code: 'ES',
    name: 'Spain',
    flag: '🇪🇸',
    postalCodePattern: /^\d{5}$/,
    postalCodeFormat: '12345',
    postalCodePlaceholder: '28001',
    postalCodeRequired: true,
    hasStates: false,
    stateLabel: 'Province',
  },
  {
    code: 'PT',
    name: 'Portugal',
    flag: '🇵🇹',
    postalCodePattern: /^\d{4}-?\d{3}$/,
    postalCodeFormat: '1234-567',
    postalCodePlaceholder: '1234-567',
    postalCodeRequired: true,
    hasStates: false,
    stateLabel: 'District',
  },
  {
    code: 'NL',
    name: 'Netherlands',
    flag: '🇳🇱',
    postalCodePattern: /^\d{4} ?[A-Z]{2}$/i,
    postalCodeFormat: '1234 AB',
    postalCodePlaceholder: '1234 AB',
    postalCodeRequired: true,
    hasStates: false,
    stateLabel: 'Province',
  },
  {
    code: 'BE',
    name: 'Belgium',
    flag: '🇧🇪',
    postalCodePattern: /^\d{4}$/,
    postalCodeFormat: '1234',
    postalCodePlaceholder: '1000',
    postalCodeRequired: true,
    hasStates: false,
    stateLabel: 'Province',
  },
  {
    code: 'AT',
    name: 'Austria',
    flag: '🇦🇹',
    postalCodePattern: /^\d{4}$/,
    postalCodeFormat: '1234',
    postalCodePlaceholder: '1010',
    postalCodeRequired: true,
    hasStates: false,
    stateLabel: 'State',
  },
  {
    code: 'CH',
    name: 'Switzerland',
    flag: '🇨🇭',
    postalCodePattern: /^\d{4}$/,
    postalCodeFormat: '1234',
    postalCodePlaceholder: '8001',
    postalCodeRequired: true,
    hasStates: false,
    stateLabel: 'Canton',
  },
  {
    code: 'SE',
    name: 'Sweden',
    flag: '🇸🇪',
    postalCodePattern: /^\d{3} ?\d{2}$/,
    postalCodeFormat: '123 45',
    postalCodePlaceholder: '123 45',
    postalCodeRequired: true,
    hasStates: false,
    stateLabel: 'County',
  },
  {
    code: 'NO',
    name: 'Norway',
    flag: '🇳🇴',
    postalCodePattern: /^\d{4}$/,
    postalCodeFormat: '1234',
    postalCodePlaceholder: '0123',
    postalCodeRequired: true,
    hasStates: false,
    stateLabel: 'County',
  },
  {
    code: 'DK',
    name: 'Denmark',
    flag: '🇩🇰',
    postalCodePattern: /^\d{4}$/,
    postalCodeFormat: '1234',
    postalCodePlaceholder: '1234',
    postalCodeRequired: true,
    hasStates: false,
    stateLabel: 'Region',
  },
  {
    code: 'FI',
    name: 'Finland',
    flag: '🇫🇮',
    postalCodePattern: /^\d{5}$/,
    postalCodeFormat: '12345',
    postalCodePlaceholder: '00100',
    postalCodeRequired: true,
    hasStates: false,
    stateLabel: 'Region',
  },
  {
    code: 'PL',
    name: 'Poland',
    flag: '🇵🇱',
    postalCodePattern: /^\d{2}-?\d{3}$/,
    postalCodeFormat: '12-345',
    postalCodePlaceholder: '00-001',
    postalCodeRequired: true,
    hasStates: false,
    stateLabel: 'Voivodeship',
  },
  {
    code: 'CZ',
    name: 'Czech Republic',
    flag: '🇨🇿',
    postalCodePattern: /^\d{3} ?\d{2}$/,
    postalCodeFormat: '123 45',
    postalCodePlaceholder: '110 00',
    postalCodeRequired: true,
    hasStates: false,
    stateLabel: 'Region',
  },
  {
    code: 'GR',
    name: 'Greece',
    flag: '🇬🇷',
    postalCodePattern: /^\d{5}$/,
    postalCodeFormat: '12345',
    postalCodePlaceholder: '10431',
    postalCodeRequired: true,
    hasStates: false,
    stateLabel: 'Region',
  },
  {
    code: 'IE',
    name: 'Ireland',
    flag: '🇮🇪',
    postalCodePattern: /^[A-Z]\d{2} ?[A-Z\d]{4}$/i,
    postalCodeFormat: 'A12 B3C4',
    postalCodePlaceholder: 'D01 A1B2',
    postalCodeRequired: false,
    hasStates: false,
    stateLabel: 'County',
  },
  {
    code: 'RO',
    name: 'Romania',
    flag: '🇷🇴',
    postalCodePattern: /^\d{6}$/,
    postalCodeFormat: '123456',
    postalCodePlaceholder: '010001',
    postalCodeRequired: true,
    hasStates: false,
    stateLabel: 'County',
  },
  {
    code: 'HU',
    name: 'Hungary',
    flag: '🇭🇺',
    postalCodePattern: /^\d{4}$/,
    postalCodeFormat: '1234',
    postalCodePlaceholder: '1011',
    postalCodeRequired: true,
    hasStates: false,
    stateLabel: 'County',
  },
  {
    code: 'RU',
    name: 'Russia',
    flag: '🇷🇺',
    postalCodePattern: /^\d{6}$/,
    postalCodeFormat: '123456',
    postalCodePlaceholder: '101000',
    postalCodeRequired: true,
    hasStates: false,
    stateLabel: 'Region',
  },
  {
    code: 'UA',
    name: 'Ukraine',
    flag: '🇺🇦',
    postalCodePattern: /^\d{5}$/,
    postalCodeFormat: '12345',
    postalCodePlaceholder: '01001',
    postalCodeRequired: true,
    hasStates: false,
    stateLabel: 'Oblast',
  },
  {
    code: 'TR',
    name: 'Turkey',
    flag: '🇹🇷',
    postalCodePattern: /^\d{5}$/,
    postalCodeFormat: '12345',
    postalCodePlaceholder: '34000',
    postalCodeRequired: true,
    hasStates: false,
    stateLabel: 'Province',
  },
  
  // Asia Pacific
  {
    code: 'JP',
    name: 'Japan',
    flag: '🇯🇵',
    postalCodePattern: /^\d{3}-?\d{4}$/,
    postalCodeFormat: '123-4567',
    postalCodePlaceholder: '100-0001',
    postalCodeRequired: true,
    hasStates: false,
    stateLabel: 'Prefecture',
  },
  {
    code: 'CN',
    name: 'China',
    flag: '🇨🇳',
    postalCodePattern: /^\d{6}$/,
    postalCodeFormat: '123456',
    postalCodePlaceholder: '100000',
    postalCodeRequired: true,
    hasStates: false,
    stateLabel: 'Province',
  },
  {
    code: 'IN',
    name: 'India',
    flag: '🇮🇳',
    postalCodePattern: /^\d{6}$/,
    postalCodeFormat: '123456',
    postalCodePlaceholder: '110001',
    postalCodeRequired: true,
    hasStates: false,
    stateLabel: 'State',
  },
  {
    code: 'KR',
    name: 'South Korea',
    flag: '🇰🇷',
    postalCodePattern: /^\d{5}$/,
    postalCodeFormat: '12345',
    postalCodePlaceholder: '03000',
    postalCodeRequired: true,
    hasStates: false,
    stateLabel: 'Province',
  },
  {
    code: 'AU',
    name: 'Australia',
    flag: '🇦🇺',
    postalCodePattern: /^\d{4}$/,
    postalCodeFormat: '1234',
    postalCodePlaceholder: '2000',
    postalCodeRequired: true,
    hasStates: true,
    stateLabel: 'State',
    states: AU_STATES,
  },
  {
    code: 'NZ',
    name: 'New Zealand',
    flag: '🇳🇿',
    postalCodePattern: /^\d{4}$/,
    postalCodeFormat: '1234',
    postalCodePlaceholder: '1010',
    postalCodeRequired: true,
    hasStates: false,
    stateLabel: 'Region',
  },
  {
    code: 'SG',
    name: 'Singapore',
    flag: '🇸🇬',
    postalCodePattern: /^\d{6}$/,
    postalCodeFormat: '123456',
    postalCodePlaceholder: '018956',
    postalCodeRequired: true,
    hasStates: false,
    stateLabel: 'District',
  },
  {
    code: 'MY',
    name: 'Malaysia',
    flag: '🇲🇾',
    postalCodePattern: /^\d{5}$/,
    postalCodeFormat: '12345',
    postalCodePlaceholder: '50000',
    postalCodeRequired: true,
    hasStates: false,
    stateLabel: 'State',
  },
  {
    code: 'TH',
    name: 'Thailand',
    flag: '🇹🇭',
    postalCodePattern: /^\d{5}$/,
    postalCodeFormat: '12345',
    postalCodePlaceholder: '10100',
    postalCodeRequired: true,
    hasStates: false,
    stateLabel: 'Province',
  },
  {
    code: 'ID',
    name: 'Indonesia',
    flag: '🇮🇩',
    postalCodePattern: /^\d{5}$/,
    postalCodeFormat: '12345',
    postalCodePlaceholder: '10110',
    postalCodeRequired: true,
    hasStates: false,
    stateLabel: 'Province',
  },
  {
    code: 'PH',
    name: 'Philippines',
    flag: '🇵🇭',
    postalCodePattern: /^\d{4}$/,
    postalCodeFormat: '1234',
    postalCodePlaceholder: '1000',
    postalCodeRequired: true,
    hasStates: false,
    stateLabel: 'Province',
  },
  {
    code: 'VN',
    name: 'Vietnam',
    flag: '🇻🇳',
    postalCodePattern: /^\d{6}$/,
    postalCodeFormat: '123456',
    postalCodePlaceholder: '100000',
    postalCodeRequired: false,
    hasStates: false,
    stateLabel: 'Province',
  },
  {
    code: 'HK',
    name: 'Hong Kong',
    flag: '🇭🇰',
    postalCodeFormat: 'Not required',
    postalCodePlaceholder: '',
    postalCodeRequired: false,
    hasStates: false,
    stateLabel: 'District',
  },
  {
    code: 'TW',
    name: 'Taiwan',
    flag: '🇹🇼',
    postalCodePattern: /^\d{3,5}$/,
    postalCodeFormat: '123 or 12345',
    postalCodePlaceholder: '100',
    postalCodeRequired: true,
    hasStates: false,
    stateLabel: 'County',
  },
  
  // Middle East & Africa
  {
    code: 'AE',
    name: 'United Arab Emirates',
    flag: '🇦🇪',
    postalCodeFormat: 'Not required',
    postalCodePlaceholder: '',
    postalCodeRequired: false,
    hasStates: false,
    stateLabel: 'Emirate',
  },
  {
    code: 'SA',
    name: 'Saudi Arabia',
    flag: '🇸🇦',
    postalCodePattern: /^\d{5}(-\d{4})?$/,
    postalCodeFormat: '12345',
    postalCodePlaceholder: '12345',
    postalCodeRequired: false,
    hasStates: false,
    stateLabel: 'Province',
  },
  {
    code: 'IL',
    name: 'Israel',
    flag: '🇮🇱',
    postalCodePattern: /^\d{7}$/,
    postalCodeFormat: '1234567',
    postalCodePlaceholder: '1234567',
    postalCodeRequired: true,
    hasStates: false,
    stateLabel: 'District',
  },
  {
    code: 'EG',
    name: 'Egypt',
    flag: '🇪🇬',
    postalCodePattern: /^\d{5}$/,
    postalCodeFormat: '12345',
    postalCodePlaceholder: '11511',
    postalCodeRequired: false,
    hasStates: false,
    stateLabel: 'Governorate',
  },
  {
    code: 'ZA',
    name: 'South Africa',
    flag: '🇿🇦',
    postalCodePattern: /^\d{4}$/,
    postalCodeFormat: '1234',
    postalCodePlaceholder: '0001',
    postalCodeRequired: true,
    hasStates: false,
    stateLabel: 'Province',
  },
  {
    code: 'NG',
    name: 'Nigeria',
    flag: '🇳🇬',
    postalCodePattern: /^\d{6}$/,
    postalCodeFormat: '123456',
    postalCodePlaceholder: '100001',
    postalCodeRequired: false,
    hasStates: false,
    stateLabel: 'State',
  },
  {
    code: 'KE',
    name: 'Kenya',
    flag: '🇰🇪',
    postalCodePattern: /^\d{5}$/,
    postalCodeFormat: '12345',
    postalCodePlaceholder: '00100',
    postalCodeRequired: false,
    hasStates: false,
    stateLabel: 'County',
  },
  {
    code: 'MA',
    name: 'Morocco',
    flag: '🇲🇦',
    postalCodePattern: /^\d{5}$/,
    postalCodeFormat: '12345',
    postalCodePlaceholder: '10000',
    postalCodeRequired: false,
    hasStates: false,
    stateLabel: 'Region',
  },
  {
    code: 'QA',
    name: 'Qatar',
    flag: '🇶🇦',
    postalCodeFormat: 'Not required',
    postalCodePlaceholder: '',
    postalCodeRequired: false,
    hasStates: false,
    stateLabel: 'Municipality',
  },
  {
    code: 'KW',
    name: 'Kuwait',
    flag: '🇰🇼',
    postalCodePattern: /^\d{5}$/,
    postalCodeFormat: '12345',
    postalCodePlaceholder: '12345',
    postalCodeRequired: false,
    hasStates: false,
    stateLabel: 'Governorate',
  },
  {
    code: 'PK',
    name: 'Pakistan',
    flag: '🇵🇰',
    postalCodePattern: /^\d{5}$/,
    postalCodeFormat: '12345',
    postalCodePlaceholder: '44000',
    postalCodeRequired: false,
    hasStates: false,
    stateLabel: 'Province',
  },
  {
    code: 'BD',
    name: 'Bangladesh',
    flag: '🇧🇩',
    postalCodePattern: /^\d{4}$/,
    postalCodeFormat: '1234',
    postalCodePlaceholder: '1000',
    postalCodeRequired: false,
    hasStates: false,
    stateLabel: 'Division',
  },
];

// Get address config by country code
export function getAddressConfig(countryCode: string): CountryAddressConfig | undefined {
  return COUNTRY_ADDRESS_CONFIGS.find(c => c.code === countryCode);
}

/**
 * Validate a postal code for a specific country
 * @param postalCode - The postal code to validate
 * @param countryCode - ISO country code
 * @returns Validation result with isValid flag and optional error
 */
export function validatePostalCode(postalCode: string, countryCode: string): { isValid: boolean; error?: string } {
  const config = getAddressConfig(countryCode);
  
  if (!config) {
    return { isValid: true }; // Allow if no config found
  }
  
  if (!config.postalCodeRequired && !postalCode) {
    return { isValid: true };
  }
  
  if (config.postalCodeRequired && !postalCode) {
    return { isValid: false, error: 'Postal code is required' };
  }
  
  if (config.postalCodePattern && postalCode && !config.postalCodePattern.test(postalCode)) {
    return { 
      isValid: false, 
      error: `Invalid format. Expected: ${config.postalCodeFormat}` 
    };
  }
  
  return { isValid: true };
}

/**
 * Get states/provinces for a country
 * @param countryCode - ISO country code
 * @returns Array of state/province objects or undefined
 */
export function getStatesForCountry(countryCode: string): { code: string; name: string }[] | undefined {
  const config = getAddressConfig(countryCode);
  return config?.states;
}

/**
 * Check if a country has states/provinces
 * @param countryCode - ISO country code
 * @returns True if country has states
 */
export function countryHasStates(countryCode: string): boolean {
  const config = getAddressConfig(countryCode);
  return config?.hasStates ?? false;
}

/**
 * Get state label for a country (State, Province, Region, etc.)
 * @param countryCode - ISO country code
 * @returns State label string
 */
export function getStateLabel(countryCode: string): string {
  const config = getAddressConfig(countryCode);
  return config?.stateLabel ?? 'State/Province';
}

/**
 * Format postal code for display
 * @param postalCode - Raw postal code
 * @param countryCode - ISO country code
 * @returns Formatted postal code
 */
export function formatPostalCode(postalCode: string, countryCode: string): string {
  if (!postalCode) return '';
  
  const config = getAddressConfig(countryCode);
  if (!config) return postalCode;
  
  // Apply country-specific formatting
  switch (countryCode) {
    case 'CA':
      // Format as A1A 1A1
      const ca = postalCode.replace(/\s/g, '').toUpperCase();
      if (ca.length === 6) {
        return `${ca.slice(0, 3)} ${ca.slice(3)}`;
      }
      return ca;
    case 'GB':
      // Format UK postcodes with space
      const uk = postalCode.replace(/\s/g, '').toUpperCase();
      if (uk.length >= 5) {
        return `${uk.slice(0, -3)} ${uk.slice(-3)}`;
      }
      return uk;
    case 'BR':
      // Format as 12345-678
      const br = postalCode.replace(/\D/g, '');
      if (br.length === 8) {
        return `${br.slice(0, 5)}-${br.slice(5)}`;
      }
      return br;
    case 'JP':
      // Format as 123-4567
      const jp = postalCode.replace(/\D/g, '');
      if (jp.length === 7) {
        return `${jp.slice(0, 3)}-${jp.slice(3)}`;
      }
      return jp;
    case 'PT':
      // Format as 1234-567
      const pt = postalCode.replace(/\D/g, '');
      if (pt.length === 7) {
        return `${pt.slice(0, 4)}-${pt.slice(4)}`;
      }
      return pt;
    case 'PL':
      // Format as 12-345
      const pl = postalCode.replace(/\D/g, '');
      if (pl.length === 5) {
        return `${pl.slice(0, 2)}-${pl.slice(2)}`;
      }
      return pl;
    default:
      return postalCode;
  }
}

// List of all countries sorted alphabetically
export const COUNTRIES_LIST = COUNTRY_ADDRESS_CONFIGS
  .map(c => ({ code: c.code, name: c.name, flag: c.flag }))
  .sort((a, b) => a.name.localeCompare(b.name));

// Popular countries for quick access
export const POPULAR_COUNTRIES = ['US', 'CA', 'GB', 'MX', 'AR', 'BR', 'DE', 'FR', 'ES', 'IT', 'AU', 'JP', 'CN', 'IN'];

// Get countries with popular ones first
export function getCountriesWithPopularFirst(): { code: string; name: string; flag: string }[] {
  const popular = COUNTRY_ADDRESS_CONFIGS
    .filter(c => POPULAR_COUNTRIES.includes(c.code))
    .sort((a, b) => POPULAR_COUNTRIES.indexOf(a.code) - POPULAR_COUNTRIES.indexOf(b.code))
    .map(c => ({ code: c.code, name: c.name, flag: c.flag }));
  
  const others = COUNTRY_ADDRESS_CONFIGS
    .filter(c => !POPULAR_COUNTRIES.includes(c.code))
    .sort((a, b) => a.name.localeCompare(b.name))
    .map(c => ({ code: c.code, name: c.name, flag: c.flag }));
  
  return [...popular, ...others];
}


